import { memo, type ComponentProps } from 'react';
import type { SemanticColorToken } from '@vitvio/design-system';
import { CoreBox, CoreChip, CoreIcon, CoreText } from '@vitvio/design-system';
import { useTranslation } from '@vitvio/locales';
import { formatDurationMinutes, isSameMinute, type Dayjs, type Nullable } from '@vitvio/helpers';
import { Time } from '@vitvio/time-components';
import type { EventData, JobScheduleStatusEnum, JobStatusEnum } from '../../types';

type IconName = ComponentProps<typeof CoreIcon>['icon'];

interface TimeProps {
  type: 'time';
  status: Nullable<JobStatusEnum>;
  scheduleStatus: Nullable<JobScheduleStatusEnum>;
  plannedTime: Nullable<Dayjs>;
  estimatedTime: Nullable<Dayjs>;
  actualTime: Nullable<Dayjs>;
  position: 'start' | 'end';
  isJobUnclosed?: boolean;
}

interface DurationProps {
  type: 'duration';
  duration: number;
  plannedDuration: number;
  isTurnover?: boolean;
}

interface LastEventProps {
  type: 'lastEvent';
  lastEvent: EventData;
}

type Props = TimeProps | DurationProps | LastEventProps;

interface ComputedValues {
  icon: IconName | null;
  labelPath?: string;
  eventLabelPath?: string;
  /** Formatted string for duration; used when isTimeValue is false. */
  mainText?: string;
  /** Time value for Time component; used when isTimeValue is true. */
  mainTime?: Dayjs;
  /** Strikethrough string for duration. */
  strikethroughText?: string;
  /** Strikethrough time for Time component. */
  strikethroughTime?: Dayjs;
  highlightedBg?: SemanticColorToken;
  isTimeValue: boolean;
  timeColor: SemanticColorToken;
}

function computeValues(props: Props, t: ReturnType<typeof useTranslation>['t']): ComputedValues {
  if (props.type === 'duration') {
    const { duration, plannedDuration, isTurnover } = props;

    const labelPath = isTurnover
      ? 'missionControl.infoBox.duration.label.turnover'
      : 'missionControl.infoBox.duration.label.duration';

    const hasDifference = duration !== plannedDuration;

    return {
      icon: 'timer',
      labelPath,
      mainText: formatDurationMinutes(duration, t),
      strikethroughText: hasDifference ? formatDurationMinutes(plannedDuration, t) : undefined,
      isTimeValue: false,
      timeColor: 'fg',
    };
  }

  if (props.type === 'time') {
    const { status, scheduleStatus, plannedTime, estimatedTime, actualTime, position, isJobUnclosed } = props;

    type TimeType = 'actual' | 'estimated' | 'planned' | 'unknown';
    let timeType: TimeType;
    let time: Nullable<Dayjs>;

    if (actualTime) {
      timeType = 'actual';
      time = actualTime;
    } else if (isJobUnclosed || estimatedTime || (plannedTime && status === 'RUNNING')) {
      timeType = 'estimated';
      time = isJobUnclosed ? null : estimatedTime || (status === 'RUNNING' ? plannedTime : null);
    } else if (plannedTime) {
      timeType = 'planned';
      time = plannedTime;
    } else {
      timeType = 'unknown';
      time = null;
    }

    const labelMap = {
      actual: position === 'start' ? 'actualStart' : 'actualEnd',
      estimated: position === 'start' ? 'estimatedStart' : 'estimatedEnd',
      planned: position === 'start' ? 'plannedStart' : 'plannedEnd',
      unknown: 'unknown',
    } as const;

    const hasDifference = Boolean(time) && !isSameMinute(time, plannedTime);

    let highlightedBg: SemanticColorToken | undefined;
    if (isJobUnclosed && !actualTime) {
      highlightedBg = 'bg.warning.solid';
    } else if (status === 'SCHEDULED') {
      if (scheduleStatus === 'AHEAD' && hasDifference) {
        highlightedBg = 'card.bg.ahead';
      } else if (hasDifference) {
        highlightedBg = 'bg.warning.solid';
      }
    }

    return {
      icon: 'schedule',
      labelPath: `missionControl.infoBox.time.${labelMap[timeType]}`,
      mainTime: time ?? undefined,
      strikethroughTime:
        hasDifference || (isJobUnclosed && !actualTime && plannedTime) ? (plannedTime ?? undefined) : undefined,
      highlightedBg,
      isTimeValue: true,
      timeColor: highlightedBg === 'bg.warning.solid' ? 'fg.constant' : 'fg',
    };
  }

  // lastEvent (only rendered when lastEvent is defined)
  const { eventTypeName, happenedAt } = props.lastEvent;
  return {
    icon: null,
    eventLabelPath: `events.eventTypes.${eventTypeName}`,
    mainTime: happenedAt,
    isTimeValue: true,
    timeColor: 'fg.muted',
  };
}

function arePropsEqual(prev: Props, next: Props): boolean {
  if (prev.type !== next.type) return false;

  if (prev.type === 'duration' && next.type === 'duration') {
    return (
      prev.duration === next.duration &&
      prev.plannedDuration === next.plannedDuration &&
      prev.isTurnover === next.isTurnover
    );
  }
  if (prev.type === 'time' && next.type === 'time') {
    return (
      prev.status === next.status &&
      prev.scheduleStatus === next.scheduleStatus &&
      prev.plannedTime === next.plannedTime &&
      prev.estimatedTime === next.estimatedTime &&
      prev.actualTime === next.actualTime &&
      prev.position === next.position &&
      prev.isJobUnclosed === next.isJobUnclosed
    );
  }
  if (prev.type === 'lastEvent' && next.type === 'lastEvent') {
    return (
      prev.lastEvent.eventTypeName === next.lastEvent.eventTypeName &&
      prev.lastEvent.happenedAt === next.lastEvent.happenedAt
    );
  }
  return false;
}

export const InfoBoxItem = memo(function InfoBoxItem(props: Props) {
  const { t } = useTranslation();

  const {
    icon,
    labelPath,
    eventLabelPath,
    mainText,
    mainTime,
    strikethroughText,
    strikethroughTime,
    highlightedBg,
    isTimeValue,
    timeColor,
  } = computeValues(props, t);

  return (
    <CoreBox flexRow justifyContent="space-between" alignItems="center">
      <CoreBox flexRow alignItems="center" gap="2">
        {icon && <CoreIcon icon={icon} size="md" />}
        {labelPath && <CoreText path={labelPath} font="S_Regular" color="fg" />}
        {eventLabelPath && <CoreChip path={eventLabelPath} variant="solid" size="sm" color="default" />}
      </CoreBox>
      <CoreBox flexRow gap="3" alignItems="center">
        {isTimeValue
          ? strikethroughTime && (
              <Time date={strikethroughTime} font="metric.S_Regular" color="fg.muted" strikeThrough />
            )
          : strikethroughText && (
              <CoreText font="metric.S_Regular" color="fg.muted" strikeThrough>
                {strikethroughText}
              </CoreText>
            )}
        <CoreBox inlineFlex alignItems="center" bg={highlightedBg} borderRadius="xs" px="1" py="0.5">
          {isTimeValue ? (
            <Time date={mainTime} font="metric.S_Medium" color={timeColor} />
          ) : (
            <CoreText font="metric.S_Medium" color={timeColor}>
              {mainText}
            </CoreText>
          )}
        </CoreBox>
      </CoreBox>
    </CoreBox>
  );
}, arePropsEqual);
